import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import ReactDOM from "react-dom";
import { Icons } from "../../../design_system/components/Icons";
import { Button } from "../../../design_system/components/Button/Button.jsx";
import { Popup } from "../../../design_system/components/Popup/Popup.jsx";
import { PopupContext } from "../../../design_system/components/Popup/PopupContext";
import useCheckUserAccess from "../../custom_hooks/useCheckUserAccess";
import {
    createLayerId,
    pushLayer,
    popLayer,
    isTopLayer,
    getLayerZIndex,
    addScrollLock,
    removeScrollLock,
} from "../../../design_system/components/Popup/popupStack";
import { TabItem } from "../../../design_system/components/Tabs/TabItem";
import { SettingsProvider, useSettingsContext } from "./SettingsContext";
import ManageProfile from "./ManageProfile";
import ManageNotifications from "./ManageNotifications";
import ManageDataFields from "./ManageDataFields";
import ManageTeams from "./ManageTeams";
import RolePermissions from "./access/RolePermissions";
import { TeamNotifications } from "./TeamNotifications";
import TeamSettings from "./TeamSettings";
import ConfigurationTab from "./_ConfigurationTab";
import { canUseAnyMemberManagementAction, usesPolicyAuthorization } from "../../../utils/access";
import ControlsTab from "./_ControlsTab";

// ── Tab & section definitions ─────────────────────────────────────────────────
// access: key from useCheckUserAccess — omit means always visible

const TABS = {
    profile:            { label: "Profile",            section: "account", iconName: "user-circle" },
    notifications:      { label: "Notifications",      section: "account", iconName: "notifications" },
    data_fields:        { label: "Data Fields",        section: "team", iconName: "user-attribute" },
    people:             { label: "People",             section: "team",     memberManagement: true, legacyAccess: "team_managing", iconName: "users-group" },
    roles_permissions:  { label: "Roles & Permissions", section: "team",     teamAdminOnly: true, policyAuthorization: true, iconName: "roles-permissions" },
    team_notifications: { label: "Team Notifications", section: "team",     access: "team_notification", iconName: "notifications" },
    team_settings:      { label: "Settings",           section: "team",     access: "team_setting", iconName: "settings" },
    configuration:      { label: "Configuration",      section: "internal", access: "super_admin_panel", iconName: "config" },
    controls:           { label: "Controls",           section: "internal", access: "super_admin_panel", iconName: "controls" },
};

const SECTIONS = [
    { key: "account",  label: "Account",  tabs: ["profile", "notifications"] },
    { key: "team",     label: "Team",     tabs: ["data_fields", "people", "roles_permissions", "team_notifications", "team_settings"] },
    { key: "internal", label: "Internal", tabs: ["configuration", "controls"] },
];

// ── Sidebar (inside SettingsProvider so it can check hasChanges) ─────────────

const Sidebar = ({ activeTab, setActiveTab, isTabVisible, isSectionVisible, guardUnsavedChanges }) => {
    const handleTabClick = (tabKey) => {
        if (tabKey === activeTab) return;
        guardUnsavedChanges(() => setActiveTab(tabKey));
    };

    return (
        <aside className="settings-popup__sidebar">
            {SECTIONS.filter(isSectionVisible).map((section) => (
                <div key={section.key} className="settings-popup__section">
                    <span className="settings-popup__section-label support-m-caps">
                        {section.label}
                    </span>
                    {section.tabs.filter(isTabVisible).map((tabKey) => (
                        <TabItem
                            iconName={TABS[tabKey].iconName}
                            key={tabKey}
                            variant="block-mono"
                            isActive={activeTab === tabKey}
                            onClick={() => handleTabClick(tabKey)}
                            label={TABS[tabKey].label}
                            customClass="settings-popup__nav-item"
                        />
                    ))}
                </div>
            ))}
        </aside>
    );
};

// ── Tab content ───────────────────────────────────────────────────────────────
// All legacy components call props.setActiveSettingTab() on mount — pass a
// no-op so they don't error; the popup manages its own activeTab state.

const NOOP = () => {};

const TabContent = ({ activeTab, onClose, switchTab }) => {
    const sharedProps = { setActiveSettingTab: NOOP, onClose, switchTab };

    switch (activeTab) {
        case "profile":
            return <ManageProfile {...sharedProps} />;
        case "notifications":
            return <ManageNotifications {...sharedProps} />;
        case "data_fields":
            return <ManageDataFields {...sharedProps} />;
        case "people":
            return <ManageTeams {...sharedProps} />;
        case "roles_permissions":
            return <RolePermissions {...sharedProps} />;
        case "team_notifications":
            return <TeamNotifications {...sharedProps} />;
        case "team_settings":
            return <TeamSettings {...sharedProps} />;
        case "configuration":
            return <ConfigurationTab />;
        case "controls":
            return <ControlsTab />;
        default:
            return null;
    }
};

// ── Save footer (shown when tab has unsaved changes) ─────────────────────────

const SaveFooter = () => {
    const { hasChanges, isSaving, triggerSave, triggerCancel } = useSettingsContext();
    if (!hasChanges) return null;

    return (
        <div className="settings-popup__footer">
            <Button Type="Secondary" Size="Medium" onClick={triggerCancel} disabled={isSaving}>
                Cancel
            </Button>
            <Button Type="Primary" Size="Medium" onClick={triggerSave} isLoading={isSaving}>
                Save
            </Button>
        </div>
    );
};

// ── Inner component (inside SettingsProvider — has context access) ────────────

const SettingsPopupInner = ({ activeTab, setActiveTab, onClose, isTabVisible, isSectionVisible, layerIdRef, handleCloseRef }) => {
    const { hasChanges, isSaving, triggerSave, triggerCancel } = useSettingsContext();
    const [showUnsavedPopup, setShowUnsavedPopup] = useState(false);
    const pendingActionRef = useRef(null);
    const hasChangesRef = useRef(hasChanges);
    hasChangesRef.current = hasChanges;

    const guardUnsavedChanges = useCallback((action) => {
        if (!hasChanges) {
            action();
            return;
        }
        pendingActionRef.current = action;
        setShowUnsavedPopup(true);
    }, [hasChanges]);

    const handleClose = useCallback(() => {
        guardUnsavedChanges(() => onClose?.());
    }, [guardUnsavedChanges, onClose]);

    // Expose handleClose to parent for backdrop click
    useEffect(() => {
        handleCloseRef.current = handleClose;
    }, [handleClose, handleCloseRef]);

    // ── Keyboard handler ──────────────────────────────────────────────────────

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape" && isTopLayer(layerIdRef.current)) {
                e.stopPropagation();
                handleClose();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [handleClose, layerIdRef]);

    return (
        <>
            {/* ── Sidebar ── */}
            <Sidebar
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                isTabVisible={isTabVisible}
                isSectionVisible={isSectionVisible}
                guardUnsavedChanges={guardUnsavedChanges}
            />

            {/* ── Main panel ── */}
            <div className="settings-popup__main">
                <div
                    className="close_button settings-popup__close"
                    onClick={handleClose}
                    role="button"
                    aria-label="Close settings"
                >
                    <Icons name="close" size={16} />
                </div>
                <div className="settings-popup__content">
                    <div className="settings-popup__inner">
                        <TabContent activeTab={activeTab} onClose={handleClose} switchTab={(tabKey) => guardUnsavedChanges(() => setActiveTab(tabKey))} />
                    </div>
                </div>
                <SaveFooter />
            </div>

            <Popup
                name="Unsaved Changes"
                show={showUnsavedPopup}
                handleOnClosePopup={() => setShowUnsavedPopup(false)}
                maxWidth={400}
                showHeaderDivider={false}
                showFooterDivider={false}
                tertiaryAction={{
                    label: "Cancel",
                    onClick: () => setShowUnsavedPopup(false),
                }}
                secondaryAction={{
                    label: "Discard Changes",
                    onClick: () => {
                        const action = pendingActionRef.current;
                        pendingActionRef.current = null;
                        setShowUnsavedPopup(false);
                        triggerCancel();
                        action?.();
                    },
                }}
                primaryAction={{
                    label: "Save",
                    onClick: async () => {
                        await triggerSave();
                        if (hasChangesRef.current) return; // save failed — stay on popup
                        const action = pendingActionRef.current;
                        pendingActionRef.current = null;
                        setShowUnsavedPopup(false);
                        action?.();
                    },
                    isLoading: isSaving,
                }}
            >
                <div className="body-l-semibold color-text-secondary">
                    You have unsaved changes. Please save your changes before leaving, or cancel to stay on this page.
                </div>
            </Popup>
        </>
    );
};

// ── Main component ────────────────────────────────────────────────────────────

const SettingsPopup = ({ isOpen, onClose, initialTab = "profile" }) => {
    const [activeTab, setActiveTab]     = useState(initialTab);
    const [shouldRender, setShouldRender] = useState(isOpen);
    const [isExiting, setIsExiting]     = useState(false);
    const [zIndex, setZIndex]           = useState(null);

    const layerIdRef   = useRef(createLayerId());
    const handleCloseRef = useRef(null);
    const checkUserAccess = useCheckUserAccess();
    const currentTeam = useSelector((state) => state.current_team.team);
    const currentUser = useSelector((state) => state.current_user.user);

    // ── Visibility helpers ────────────────────────────────────────────────────

    const isTabVisible = useCallback(
        (tabKey) => {
            const tab = TABS[tabKey];
            if (tab?.teamAdminOnly && currentTeam?.can_manage_access_roles !== true) return false;
            if (tab?.policyAuthorization && !usesPolicyAuthorization(currentTeam)) return false;
            if (tab?.memberManagement) {
                if (currentTeam?.is_sso_enabled === true) {
                    return checkUserAccess(tab.legacyAccess);
                }
                return canUseAnyMemberManagementAction({ user: currentUser, team: currentTeam });
            }
            return !tab?.access || checkUserAccess(tab.access);
        },
        [checkUserAccess, currentTeam, currentUser]
    );

    const isSectionVisible = useCallback(
        (section) => section.tabs.some(isTabVisible),
        [isTabVisible]
    );

    // ── Lifecycle: open / close ───────────────────────────────────────────────

    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            setIsExiting(false);
            pushLayer(layerIdRef.current);
            addScrollLock(layerIdRef.current);
            setZIndex(getLayerZIndex(layerIdRef.current));

            // Apply initialTab each time the popup opens (caller may change it)
            const targetTab = isTabVisible(initialTab) ? initialTab : null;
            if (targetTab) {
                setActiveTab(targetTab);
            } else if (!isTabVisible(activeTab)) {
                // Fall back to the first visible tab
                const fallback = SECTIONS.flatMap((s) => s.tabs).find(isTabVisible);
                if (fallback) setActiveTab(fallback);
            }
        } else if (shouldRender) {
            setIsExiting(true);
            popLayer(layerIdRef.current);
            removeScrollLock(layerIdRef.current);
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Safety cleanup on unmount
    useEffect(() => {
        const id = layerIdRef.current;
        return () => {
            popLayer(id);
            removeScrollLock(id);
        };
    }, []);

    // ── Render ────────────────────────────────────────────────────────────────

    const handleContainerAnimationEnd = () => {
        if (isExiting) setShouldRender(false);
    };

    if (!shouldRender) return null;

    // Expose popup z-index so DS Tooltip / nested popovers render ABOVE this layer
    // (base stack z-index starts at 1200, while Tooltip's CSS default is 1100).
    const popupContextValue = { close: onClose, zIndex };

    return ReactDOM.createPortal(
        <PopupContext.Provider value={popupContextValue}>
            <div
                className={`settings-popup${isExiting ? " settings-popup--exiting" : ""}`}
                style={{ zIndex }}
                tabIndex={-1}
                onClick={(e) => e.target === e.currentTarget && handleCloseRef.current?.()}
            >
                <div
                    className="settings-popup__container"
                    onAnimationEnd={handleContainerAnimationEnd}
                >
                    <SettingsProvider>
                        <SettingsPopupInner
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            onClose={onClose}
                            isTabVisible={isTabVisible}
                            isSectionVisible={isSectionVisible}
                            layerIdRef={layerIdRef}
                            handleCloseRef={handleCloseRef}
                        />
                    </SettingsProvider>
                </div>
            </div>
        </PopupContext.Provider>,
        document.body
    );
};

export default SettingsPopup;
